import { Injectable } from "@angular/core";
import {
    DataAccess,
    HttpGetRequestOptions,
    HttpPostRequestOptions,
    HttpPutRequestOptions,
} from "../../../dataaccess/data.access";
import { Item } from "../../../pojo/Item";
import { Contact } from "../../../pojo/Contact";
import { S25Util } from "../../../util/s25-util";

export interface ServiceHourEntry {
    serviceHourId: number;
    contId: number;
    contact?: Partial<Contact.Data>;
    contactName?: string;
    organizationId: number;
    organizationName?: string;
    eventId?: number;
    eventName?: string;
    activityName: string;
    serviceDate: string;
    hours: number;
    status: ServiceHoursStatus;
    description?: string;
    verifiedByContId?: number;
    verifiedByName?: string;
    denialReason?: string;
    createDt: string;
    createUser: string;
    lastModDt?: string;
    lastModUser?: string;
}

export enum ServiceHoursStatus {
    Pending = "pending",
    Approved = "approved",
    Denied = "denied",
}

export interface ServiceHourFilter {
    status?: "all" | "approved" | "pending" | "denied";
    dateRange?: "all" | "week" | "month" | "semester" | "year" | "custom";
    startDate?: string | null;
    endDate?: string | null;
    searchTerm?: string;
    organizationId?: number;
    contactId?: number;
}

export function buildServiceHourQueryParams(filter?: ServiceHourFilter): Record<string, string | number> {
    const params: Record<string, string | number> = {};

    if (!filter) {
        return params;
    }

    if (filter.status && filter.status !== "all") {
        params.status = filter.status;
    }
    if (filter.dateRange && filter.dateRange !== "all") {
        params.dateRange = filter.dateRange;
    }
    if (filter.startDate) {
        params.startDate = filter.startDate;
    }
    if (filter.endDate) {
        params.endDate = filter.endDate;
    }
    if (filter.searchTerm) {
        params.searchTerm = filter.searchTerm;
    }
    if (filter.organizationId) {
        params.organizationId = filter.organizationId;
    }
    if (filter.contactId) {
        params.contactId = filter.contactId;
    }

    return params;
}

export interface ServiceHourOrganizationPendingReviewResponse {
    hasPending?: boolean;
    err_msg?: string;
}

export interface CreateServiceHourRequest {
    organizationId: number;
    eventId?: number;
    activityName: string;
    serviceDate: Date;
    hours: number;
    description?: string;
    contId?: number;
    status?: ServiceHoursStatus;
    denialReason?: string;
    verifiedByContId?: number;
}

@Injectable({ providedIn: "root" })
export class ServiceHoursService {
    /**
     * Get service hours for the current user with optional filtering
     */
    public getContactServiceHours(params: string, httpOptions?: HttpGetRequestOptions): Promise<ServiceHourEntry[]> {
        const url = `/service-hours/contact.json${params ? "?" + params : ""}`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.getContactServiceHours");
        return DataAccess.get<ServiceHourEntry[]>(injected, httpOptions);
    }

    public getOrganizationServiceHours(
        params: string,
        httpOptions?: HttpGetRequestOptions,
    ): Promise<ServiceHourEntry[]> {
        const url = `/service-hours/organization.json?${params}`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.getOrgServiceHours");
        return DataAccess.get<ServiceHourEntry[]>(injected, httpOptions);
    }

    getServiceHours(
        itemTypeId: Item.Ids,
        itemId: number | undefined,
        filter?: ServiceHourFilter,
        httpOptions?: HttpGetRequestOptions,
    ) {
        const params = buildServiceHourQueryParams(filter);

        if (itemId) {
            const objMap: Partial<Record<Item.Ids, string>> = {
                [Item.Ids.Organization]: "organizationId",
                [Item.Ids.Event]: "eventId",
                [Item.Ids.Contact]: "contactId",
            };
            const idProp = objMap[itemTypeId];
            params[idProp!] = itemId;
        }
        const urlParams = DataAccess.params(params);
        switch (itemTypeId) {
            case Item.Ids.Organization:
                return this.getOrganizationServiceHours(urlParams, httpOptions);
            case Item.Ids.Contact:
            default:
                return this.getContactServiceHours(urlParams, httpOptions);
        }
    }

    /**
     * Cross-org service hours for SE Full FLS admins
     */
    getAdminServiceHours(filter?: ServiceHourFilter, httpOptions?: HttpGetRequestOptions): Promise<ServiceHourEntry[]> {
        const urlParams = DataAccess.params(buildServiceHourQueryParams(filter));
        const url = `/service-hours/admin.json${urlParams ? "?" + urlParams : ""}`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.getAdminServiceHours");
        return DataAccess.get<ServiceHourEntry[]>(injected, httpOptions);
    }

    hasOrganizationPendingServiceHours(organizationId: number, httpOptions?: HttpGetRequestOptions): Promise<boolean> {
        const url = `/service-hours/organization-pending-review.json?organizationId=${organizationId}`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.hasOrganizationPendingServiceHours");
        return DataAccess.get<ServiceHourOrganizationPendingReviewResponse>(injected, httpOptions).then(
            (response) => response?.hasPending === true,
        );
    }

    /**
     * Create a new service hour entry
     */
    public createServiceHour(
        entry: CreateServiceHourRequest,
        httpOptions?: HttpPostRequestOptions,
    ): Promise<ServiceHourEntry> {
        const url = `/service-hours/entry.json`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.createServiceHour");
        return DataAccess.post<ServiceHourEntry>(injected, entry, httpOptions);
    }

    /**
     * Update service hour status (approve/deny)
     */
    public updateServiceHourStatus(
        serviceHourId: number,
        status: "approved" | "pending" | "denied",
        denialReason?: string,
        httpOptions?: HttpPutRequestOptions,
    ): Promise<void> {
        const params = new URLSearchParams();
        params.append("serviceHourId", serviceHourId.toString());
        params.append("status", status);
        if (S25Util.isDefined(denialReason)) {
            params.append("denialReason", denialReason);
        }

        const url = `/service-hours/entry.json?${params.toString()}`;
        const injected = DataAccess.injectCaller(url, "ServiceHoursService.updateServiceHourStatus");
        return DataAccess.put<void>(injected, {}, httpOptions);
    }
}
